// @flow
import React from 'react'
import I18n from 'i18n-react'
import classNames from 'classnames'
import { humanizer } from 'devx-js-utilities'
import Sticky from 'react-sticky-el'

// components
import { SelectedProductBarItem } from '../../Components'
import OnlyRenderChildren from './OnlyRenderChildren'

// types
import type { QuoteRequestsType, SelectedCoveragePrices } from '../Redux/Types/QuoteStateType'
import type { InsuranceCompanyType } from '../Types/InsuranceCompanyTypes'
import type QuoteInstance from '../Controllers/QuoteInstance'

type SelectedProductBarProps = {
  insuranceCompany: InsuranceCompanyType,
  mobile: boolean,
  onRemoveSelectedProduct: (quoteKey: string) => void,
  onRemoveSupplementaryInsurances: () => void,
  onSubmit: () => void,
  quoteRequests: QuoteRequestsType,
  selectedCoveragePrices: SelectedCoveragePrices,
  selectedQuote: QuoteInstance,
  showMobileModal: boolean,
  supplementaryInsurances: { [key: string]: any },
  toggleMobileSelectedProducts: () => void,
  webTypeKey: string,
}

const INSURANCE_KEYS = ['tpl', 'cic']

const TEXTS = {
  tpl: 'general.tplNotChosen',
  cic: 'general.cicNotChosen',
  ins: 'general.supplementaryNotChosen'
}

const PRODUCT_TEXTS = {
  tpl: 'general.tpl',
  cic: 'general.cic',
  ins: 'general.chosen'
}

export default class SelectedProductBar extends React.Component<SelectedProductBarProps> {
  static defaultProps = {
    insuranceCompany: {},
    mobile: false,
    showMobileModal: false,
  }

  getSupplementaryInsurancesPrice = () => {
    const { selectedCoveragePrices, selectedQuote } = this.props
    const key = JSON.stringify(selectedQuote.subProductToTariffName)
    if (selectedCoveragePrices && selectedCoveragePrices[key]) {
      return selectedCoveragePrices[key].coveragesPrice
    }
    return 0
  }

  handleOnItemRemoveClick = (quoteKey: string) => {
    this.props.onRemoveSelectedProduct(quoteKey)
  }

  handleOnRemoveSupplementaryInsurance = () => {
    this.props.onRemoveSupplementaryInsurances()
  }

  handleOnChangeTab = (tabKey: string) => {
    this.props.onChangeTab(tabKey)
  }

  handleOnSubmit = () => {
    this.props.onSubmit()
  }

  render() {
    const { selectedQuote, mobile, showMobileModal, webTypeKey, insuranceCompany, supplementaryInsurances, coverages } = this.props
    const { toggleMobileSelectedProducts } = this.props

    if (mobile && !showMobileModal) {
      return null
    }

    const classes = ['cart-summary-wrapper']
    if (mobile) {
      classes.push('d-flex')
    } else {
      classes.push('d-none d-sm-flex')
    }
    const cartClasses = ['cart-summary fixed']
    if (mobile && showMobileModal) {
      cartClasses.push('is-open')
    }

    const priceSum = (selectedQuote.price || 0) + this.getSupplementaryInsurancesPrice()

    let RenderedComponent = OnlyRenderChildren
    if (!mobile) {
      RenderedComponent = Sticky
    }

    return ([
      <RenderedComponent
        boundaryElement='.quote-container-boundary'
        key='SelectedProductBarSticky'
        mode='bottom'
        positionRecheckInterval={1000}
        stickyClassName='sticky sticky-on-top'
      >
        <div className={classNames(cartClasses)} key='selectedProductBar-content'>
          <div className='cart-summary-mobile-wrapper d-sm-none'>
            <button className='btn btn-secondary' onClick={toggleMobileSelectedProducts} type='button'>
              {I18n.translate('general.showSummary')}
            </button>
          </div>
          <div className={classNames(classes)}>
            <div className='cart-header cart-item d-sm-none'>
              <div className='header'>
                <span className='h3'>{I18n.translate('general.selectedProducts')}</span>
                <a className='btn-link' onClick={toggleMobileSelectedProducts}>
                  <i className='pfp pfp-close d-inline-block'/>
                </a>
              </div>
            </div>
            {INSURANCE_KEYS.map((key) => {
              let icon = insuranceCompany.webLogo || ''
              let title = selectedQuote.calculatorName
              if (selectedQuote.tariffContent && selectedQuote.tariffContent[key] && selectedQuote.tariffContent[key].name) {
                const limits = selectedQuote.tariffContent[key].properties.limits
                let suffix = ''
                if (key === 'tpl') {
                  suffix = ` ${limits.health}/${limits.property}\u00a0mil.\u00a0${I18n.translate('general.currency')}`
                } else if (key === 'cic') {
                  const percent = typeof limits.percent !== 'undefined'
                  suffix = `\n${percent ? `${limits.percent}%,\u00a0min.\u00a0` : ''}${humanizer.csNumber(limits.value)}\u00a0` +
                    `${I18n.translate('general.currency')}`
                }
                title = `${selectedQuote.tariffContent[key].name[webTypeKey]}${suffix}`
                if (selectedQuote.tariffContent[key].icon && selectedQuote.tariffContent[key].icon[webTypeKey]) {
                  icon = selectedQuote.tariffContent[key].icon[webTypeKey]
                }
              }
              return (
                selectedQuote && selectedQuote.subProductToTariffName && selectedQuote.subProductToTariffName[key]
                  ? <SelectedProductBarItem
                    id={key}
                    key={selectedQuote.subProductToTariffName[key]}
                    logo={icon}
                    onRemoveClick={this.handleOnItemRemoveClick}
                    price={0}
                    productName={I18n.translate(PRODUCT_TEXTS[key])}
                    title={title}
                  />
                  : <SelectedProductBarItem
                    empty
                    emptyText={I18n.translate(TEXTS[key])}
                    id={TEXTS[key]}
                    key={TEXTS[key]}
                    onClick={() => { this.handleOnChangeTab(key) }}
                  />
              )
            })}
            {Object.keys(supplementaryInsurances).filter(supp => supp !== 'seats').length > 0
              ? <SelectedProductBarItem
                id='selected-product-bar-supp-ins'
                onRemoveClick={this.handleOnRemoveSupplementaryInsurance}
                productName={I18n.translate(PRODUCT_TEXTS.ins)}
                title={I18n.translate('general.xSupplementaryInsurances',
                  { value: Object.keys(supplementaryInsurances).filter(supp => supp !== 'seats').length })}
              />
              : <SelectedProductBarItem
                empty
                emptyText={I18n.translate(TEXTS.ins)}
                id={TEXTS.ins}
                key={TEXTS.ins}
                onClick={() => { this.handleOnChangeTab('ins') }}
              />
            }
            <div className='cart-item cart-result flex-xl-nowrap'>
            <span className='result-price text-nowrap py-3 py-sm-0'>
              <span className='d-sm-none'>{I18n.translate('general.priceSum')}: </span>
              {humanizer.csNumber(priceSum)}&nbsp;{I18n.translate('general.currency')}
            </span>
              <button
                className='btn btn-secondary px-3 text-nowrap d-sm-inline'
                onClick={this.handleOnSubmit}
                type='button'
              >
                {I18n.translate('general.arrangeOnline')}
              </button>
            </div>
          </div>
        </div>
      </RenderedComponent>,
      !mobile &&
      <SelectedProductBar
        {...this.props}
        key='selectedProductBar-mobile'
        mobile
        showMobileModal={showMobileModal}
      />
    ])
  }
}
